/**
 * Analytics Widget Utilities
 * Shared types, constants, and pure functions for the AnalyticsWidgets component.
 * No React dependencies — stateless and tree-shakeable.
 */

// ─── Types & Interfaces ───────────────────────────────────────────────

export interface WidgetData {
    id: number;
    title: string;
    price: string;
    subtitle: string;
    charttype: string;
    format: string;
    series: Array<{ date: string; value: number }>;
    weekly: Array<{ date: string; value: number }>;
    monthly: Array<{ date: string; value: number }>;
    quarterly: Array<{ date: string; value: number }>;
    yearly: Array<{ date: string; value: number }>;
    diff: string;
    trendDate: string;
}

export interface WidgetConfig {
    displayChartType: 'bar' | 'area';
    chartColor: string;
    invertColor: boolean;
    barFallbackColor: string;
}

export interface TrendColor {
    text: string;
    bg: string;
}

export interface ChartPoint {
    x: string;
    y: number;
}

export interface FetchParams {
    id: string;
    start_date: string;
    end_date: string;
    lineItems: string;
    mediaType: string;
    rateType: string;
    exchangeType: string;
    adFormat: string;
    users_id?: string;
    allowedWidgets?: string;
}

// ─── Constants ────────────────────────────────────────────────────────

export const METRICS_TITLES: readonly string[] = ['Est. Spend', 'Impressions', 'Reach', 'Clicks'] as const;
export const RATES_TITLES: readonly string[] = ['AVG. CTR', 'AVG. CPC', 'AVG. Conv. Rate', 'AVG. CPA'] as const;

export const WIDGET_CONFIG: Record<string, WidgetConfig> = {
    'Est. Spend':   { displayChartType: 'bar',  chartColor: '#05ceff', invertColor: false, barFallbackColor: '#6e6fef' },
    'Impressions':  { displayChartType: 'area', chartColor: '#038edc', invertColor: false, barFallbackColor: '#6e6fef' },
    'Reach':        { displayChartType: 'bar',  chartColor: '#6e6fef', invertColor: false, barFallbackColor: '#6e6fef' },
    'Clicks':       { displayChartType: 'area', chartColor: '#038edc', invertColor: false, barFallbackColor: '#6e6fef' },
    'AVG. CTR':     { displayChartType: 'bar',  chartColor: '#6e6fef', invertColor: false, barFallbackColor: '#038edc' },
    'AVG. CPC':     { displayChartType: 'bar',  chartColor: '#f956e9', invertColor: true,  barFallbackColor: '#038edc' },
    'AVG. Conv. Rate': { displayChartType: 'area', chartColor: '#038edc', invertColor: false, barFallbackColor: '#038edc' },
    'AVG. CVR':        { displayChartType: 'area', chartColor: '#038edc', invertColor: false, barFallbackColor: '#038edc' },
    'AVG. CPA':     { displayChartType: 'bar',  chartColor: '#f9567b', invertColor: true,  barFallbackColor: '#038edc' },
};

export const DEFAULT_METRICS_WIDGETS: WidgetData[] = [
    {
        id: 1, title: "Est. Spend", price: "$0.00", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
    {
        id: 2, title: "Impressions", price: "0", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
    {
        id: 3, title: "Reach", price: "0", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
    {
        id: 4, title: "Clicks", price: "0", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
];

export const DEFAULT_RATES_WIDGETS: WidgetData[] = [
    {
        id: 1, title: "AVG. CTR", price: "0%", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
    {
        id: 2, title: "AVG. CPC", price: "$0.00", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
    {
        id: 3, title: "AVG. Conv. Rate", price: "0%", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
    {
        id: 4, title: "AVG. CPA", price: "$0.00", subtitle: "+0%",
        charttype: "area", format: "Monthly",
        series: [], weekly: [], monthly: [], quarterly: [], yearly: [],
        diff: "0.00", trendDate: "Jan 09, 2025 - Feb 06, 2025"
    },
];

// ─── Title Mapping ────────────────────────────────────────────────────

const TITLE_MAP: Record<string, string> = {
    'AVG. Conv. Rate': 'AVG. CVR',
    'AVG. Conversion Rate': 'AVG. CVR',
    'Conv. Rate': 'CVR',
    'Conversion Rate': 'CVR',
};

export const mapTitle = (title: string): string => {
    return TITLE_MAP[title] || title;
};

// ─── Number Formatting ───────────────────────────────────────────────

export const formatNumber = (value: number): string => {
    return Math.round(value).toLocaleString();
};

export const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

export const formatPercentage = (value: number): string => {
    const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    return `${numValue.toFixed(2)}%`;
};

export const getFormattedValue = (value: number, title: string): string => {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('spend') || lowerTitle.includes('cost') || lowerTitle.includes('cpc') || lowerTitle.includes('cpa')) {
        return formatCurrency(value);
    } else if (
        lowerTitle.includes('ctr') || lowerTitle.includes('cvr') || lowerTitle.includes('rate') ||
        lowerTitle.includes('%') || lowerTitle.includes('conv') ||
        lowerTitle.includes('avg. cvr') || lowerTitle.includes('avg cvr') ||
        lowerTitle.includes('avg. ctr') || lowerTitle.includes('avg ctr')
    ) {
        return formatPercentage(value);
    } else if (lowerTitle.includes('impression') || lowerTitle.includes('click') || lowerTitle.includes('reach')) {
        return Math.round(value).toLocaleString();
    } else {
        return formatNumber(value);
    }
};

// ─── Data Helpers ─────────────────────────────────────────────────────

export const getDataByKey = (dataBy: string): string => {
    switch (dataBy) {
        case 'day': return 'series';
        case 'week': return 'weekly';
        case 'month': return 'monthly';
        case 'quarter': return 'quarterly';
        case 'year': return 'yearly';
        default: return 'weekly';
    }
};

export const getChartData = (dataPoints: Array<{ date: string; value: number }>): ChartPoint[] => {
    if (dataPoints.length === 0) {
        return [{ x: new Date().toISOString().split('T')[0], y: 0 }];
    }

    return dataPoints.map((point) => ({
        x: point.date,
        y: point.value,
    }));
};

// ─── Trend Color ──────────────────────────────────────────────────────

export const getTrendColor = (isIncrease: boolean, invertColor: boolean): TrendColor => {
    if (invertColor) {
        return isIncrease
            ? { text: '#FF0000', bg: 'rgba(255, 0, 0, 0.1)' }
            : { text: '#00C853', bg: 'rgba(0, 200, 83, 0.1)' };
    }
    return isIncrease
        ? { text: '#00C853', bg: 'rgba(0, 200, 83, 0.1)' }
        : { text: '#FF0000', bg: 'rgba(255, 0, 0, 0.1)' };
};

// ─── Chart Option Factories ──────────────────────────────────────────

export const createBarChartOptions = (
    color: string,
    categories: string[],
    tooltipFormatter: (value: number) => string
) => ({
    chart: {
        height: 50,
        type: 'bar' as const,
        sparkline: { enabled: true },
        toolbar: { show: false },
        animations: {
            enabled: true,
            easing: 'easeinout' as const,
            speed: 800,
            animateGradually: { enabled: true, delay: 150 },
            dynamicAnimation: { enabled: true, speed: 350 },
        },
    },
    plotOptions: {
        bar: { borderRadius: 6, columnWidth: '30%' },
    },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    colors: [color],
    xaxis: {
        categories,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
    },
    yaxis: { show: false },
    tooltip: {
        shared: true,
        intersect: false,
        fixed: { enabled: false },
        y: {
            title: { formatter: () => "" },
            formatter: tooltipFormatter,
        },
    },
});

export const createBarFallbackOptions = (
    color: string,
    tooltipFormatter: (value: number, opts: any) => string
) => ({
    chart: {
        type: 'bar' as const,
        height: 50,
        sparkline: { enabled: true },
        animations: {
            enabled: true,
            easing: 'easeinout' as const,
            speed: 600,
            animateGradually: { enabled: true, delay: 100 },
            dynamicAnimation: { enabled: true, speed: 250 },
        },
    },
    plotOptions: {
        bar: { columnWidth: '30%', borderRadius: 2 },
    },
    tooltip: {
        shared: true,
        intersect: false,
        fixed: { enabled: false },
        y: {
            title: { formatter: () => "" },
            formatter: tooltipFormatter,
        },
    },
    colors: [color],
    markers: { size: 0 },
});

export const createAreaOptions = (
    tooltipFormatter: (value: number, opts: any) => string
) => ({
    chart: {
        height: 50,
        type: 'area' as const,
        sparkline: { enabled: true },
        toolbar: { show: false },
        animations: {
            enabled: true,
            easing: 'easeinout' as const,
            speed: 800,
            animateGradually: { enabled: true, delay: 80 },
            dynamicAnimation: { enabled: true, speed: 300 },
        },
    },
    dataLabels: { enabled: false },
    stroke: {
        curve: 'smooth' as const,
        width: 2,
        lineCap: 'round' as const,
    },
    fill: {
        type: 'gradient',
        gradient: {
            shadeIntensity: 1,
            inverseColors: false,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [50, 100, 100, 100],
        },
    },
    tooltip: {
        shared: true,
        intersect: false,
        fixed: { enabled: false },
        y: {
            title: { formatter: () => "" },
            formatter: tooltipFormatter,
        },
    },
    colors: ['#038edc', 'transparent'],
    markers: { size: 0 },
});

// ─── Layout Utilities ────────────────────────────────────────────────

export const getRowLayout = (totalCount: number): number[] => {
    if (totalCount <= 4) return [totalCount];
    if (totalCount === 5) return [3, 2];
    if (totalCount === 6) return [3, 3];
    if (totalCount === 7) return [4, 3];
    return [4, 4];
};

export const getColWidth = (rowSize: number): number => {
    return Math.floor(12 / rowSize);
};
